import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { type ConfluenceLabel, confluenceLabelsQuery } from '@atlassian/search-client/label';
import { fetchOptionsAcrossSites } from '@atlassian/search-common/fetch-options-across-sites';

import { type CloudConfig } from '../../../../../../common/constants/filters/cloud-config/types';
import type {
	FetchedSelectFilterOption,
	LookupFetcher,
	QueryParamsFetcher,
} from '../../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../../common/constants/product-keys';

const fetchLabelsForSite = async (
	config: CloudConfig,
	cloudId: string,
	query: string,
): Promise<FetchedSelectFilterOption[]> => {
	const headers = buildAtlAttributionHeaders({
		cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const response = await confluenceLabelsQuery(cloudId, query, 10, headers);

	const suggestedLabels: ConfluenceLabel[] =
		response.data?.confluence.searchLabels.suggestedLabels || [];
	const otherLabels: ConfluenceLabel[] = response.data?.confluence.searchLabels.otherLabels || [];

	return [...suggestedLabels, ...otherLabels].map((label) => ({
		trackingKey: label.label || '',
		value: label.label || '',
		queryParamValue: label.label || '',
		label: label.label || '',
		product: config.product,
	}));
};

export const confluenceSearchLabels: LookupFetcher = async (query: string, config: CloudConfig) => {
	if (!config.intl || config.cloudId === undefined) {
		// base on existing interface, can only query contributors with intl
		return [];
	}

	const product = config.product;
	if (product === ProductKeys.ConfluenceDC) {
		return [
			{
				trackingKey: query,
				value: query,
				label: query,
				queryParamValue: query,
				product: config.product,
			},
		];
	}

	// Labels are plain strings, so the same label on two sites is one option that filters both.
	return fetchOptionsAcrossSites(config, (cloudId) => fetchLabelsForSite(config, cloudId, query));
};

export const confluenceQueryParamsFetcher: QueryParamsFetcher = async (queryParams) => {
	return queryParams.map((label) => ({
		trackingKey: label,
		value: label,
		queryParamValue: label,
		label: label,
	}));
};
